import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { lighten, makeStyles } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  Checkbox,
  Icon,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Link,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { Skeleton } from '@material-ui/lab'
import moment from 'moment'
import 'moment/locale/fr'
import { AuthContext } from '../AuthContext'
import { ENUM_USER_ROLE } from '../graphql/user'
import useSnackbar from '../hooks/useSnackar'

const LINE_HEIGHT = 60

const loadingUsers = [
  {
    id: 1,
    title: <Skeleton />,
    start_date: <Skeleton />,
    end_date: <Skeleton />,
    owner: {
      company: <Skeleton />,
    },
  },
  {
    id: 2,
    title: <Skeleton />,
    start_date: <Skeleton />,
    end_date: <Skeleton />,
    owner: {
      company: <Skeleton />,
    },
  },
  {
    id: 3,
    title: <Skeleton />,
    start_date: <Skeleton />,
    end_date: <Skeleton />,
    owner: {
      company: <Skeleton />,
    },
  },
  {
    id: 4,
    title: <Skeleton />,
    start_date: <Skeleton />,
    end_date: <Skeleton />,
    owner: {
      company: <Skeleton />,
    },
  },
  {
    id: 5,
    title: <Skeleton />,
    start_date: <Skeleton />,
    end_date: <Skeleton />,
    owner: {
      company: <Skeleton />,
    },
  },
]

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const headCells = [
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'id', numeric: false, disablePadding: true, label: 'ID' },
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'title', numeric: false, disablePadding: false, label: 'Titre' },
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'uniqueResponse', numeric: false, disablePadding: false, label: 'Une réponse par appareil' },
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'start_date', numeric: false, disablePadding: false, label: 'Date de début' },
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'end_date', numeric: false, disablePadding: false, label: 'Date de fin' },
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'nbQuestions', numeric: false, disablePadding: false, label: 'Nombre de questions' },
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'nbAnswers', numeric: false, disablePadding: false, label: 'Nombre de réponses' },
  { roles: [ENUM_USER_ROLE.ADMIN], id: 'company', numeric: false, disablePadding: false, label: 'Entreprise' },
  { roles: [ENUM_USER_ROLE.CLIENT, ENUM_USER_ROLE.ADMIN], id: 'slug', numeric: false, disablePadding: false, label: 'Lien' },
]

const findHeadCellById = (id) => {
  return headCells.find((item) => item.id === id)
}

function EnhancedTableHead(props) {
  const { classes, userRole, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  const roleFilter = (item) => item.roles.includes(userRole)

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all desserts' }}
          />
        </TableCell>
        {headCells.filter(roleFilter).map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
}

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
        color: theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: '1 1 100%',
  },
}))

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles()
  const { numSelected, handleDelete, textFilter, handleTextFilterChange } = props

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
          {numSelected} ligne(s) sélectionnée(s)
        </Typography>
      ) : (
        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
          Sondages
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete" onClick={handleDelete}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <>
          <TextField
            value={textFilter}
            onChange={handleTextFilterChange}
            label="Rechercher"
            variant="outlined"
            margin="dense"
            style={{ minWidth: '250px' }}
            placeholder="Titre, type ou entreprise"
          />
        </>
      )}
    </Toolbar>
  )
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}))

function Row(props) {
  const { row, downloadSurvey, index, userRole, handleSelectClick, handleRowClick, isSelected } = props
  const isItemSelected = isSelected(row.id)
  const labelId = `enhanced-table-checkbox-${index}`
  const { addSuccessSnackbar } = useSnackbar()
  return (
    <TableRow
      hover
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
    >
      <TableCell padding="checkbox" onClick={(event) => handleSelectClick(event, row.id)}>
        <Checkbox
          checked={isItemSelected}
          inputProps={{ 'aria-labelledby': labelId }}
        />
      </TableCell>
      {findHeadCellById('id')?.roles?.includes(userRole) && (<TableCell component="th" id={labelId} scope="row" padding="none" onClick={() => handleRowClick(row)}>
        {row.id}
      </TableCell>)}
      {findHeadCellById('title')?.roles?.includes(userRole) && <TableCell align="left" onClick={() => handleRowClick(row)}>{row.title}</TableCell>}
      {findHeadCellById('uniqueResponse')?.roles?.includes(userRole) && <TableCell align="left" onClick={() => handleRowClick(row)}>{row.uniqueResponse ? 'Oui' : 'Non'}</TableCell>}
      {findHeadCellById('start_date')?.roles?.includes(userRole) && <TableCell align="left" onClick={() => handleRowClick(row)}>{row.start_date ? moment(row.start_date).locale('fr').format('LLLL') : '-'}</TableCell>}
      {findHeadCellById('end_date')?.roles?.includes(userRole) && <TableCell align="left" onClick={() => handleRowClick(row)}>{row.end_date ? moment(row.end_date).locale('fr').format('LLLL') : '-'}</TableCell>}
      {findHeadCellById('nbQuestions')?.roles?.includes(userRole) && <TableCell align="left" onClick={() => handleRowClick(row)}>{row.questions?.length}</TableCell>}
      {findHeadCellById('nbAnswers')?.roles?.includes(userRole) && <TableCell align="left" onClick={() => handleRowClick(row)}>{row.answers?.length}</TableCell>}
      {findHeadCellById('company')?.roles?.includes(userRole) && <TableCell align="left" onClick={() => handleRowClick(row)}>{row.owner?.company}</TableCell>}
      {findHeadCellById('slug')?.roles?.includes(userRole) && (
        <TableCell align="left">
          <Button color="primary" variant="contained" onClick={() => {
            navigator.clipboard.writeText(`${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_APP_URL}/public/sondages/${row.slug}`)
            addSuccessSnackbar('Lien copié')
          }}>
            Copier le lien
          </Button>
          <Button
            href={`${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_APP_URL}/public/sondages/${row.slug}`}
            target="_blank"
            variant="contained"
            color="primary"
            component={Link}
            style={{ marginLeft: '16px' }}
            endIcon={<Icon>launch</Icon>}
          >
            Aperçu
          </Button>
          <Button
            onClick={downloadSurvey(row.id)}
            variant="contained"
            color="primary"
            style={{ marginTop: '10px' }}
            endIcon={<Icon>download</Icon>}
          >
            Exporter
          </Button>
        </TableCell>
      )}
    </TableRow>
  )
}

export default function SurveyTable({ rows, deleteSurveys, downloadSurvey, loading, handleRowClick }) {
  const { addSuccessSnackbar } = useSnackbar()
  const { authState } = useContext(AuthContext)
  const headers = new Headers()
  headers.append('authorization', `Bearer ${authState.token}`)
  const classes = useStyles()
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('order')
  const [selected, setSelected] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }

    setSelected(newSelected)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const isSelected = (id) => selected.indexOf(id) !== -1

  const [textFilter, setTextFilter] = useState("")

  const emptyRows = rows.length < rowsPerPage ? 0 : rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage)

  const filterRows = (item) => {
    if (textFilter !== "") {
      return item?.owner?.company?.toLowerCase().includes(textFilter.toLowerCase()) || item?.name?.toLowerCase().includes(textFilter.toLocaleLowerCase()) || item?.type?.toLowerCase().includes(textFilter.toLocaleLowerCase())
    }
    return true
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          handleDelete={() => {
            deleteSurveys({ variables: { ids: selected.map(item => parseInt(item)) } }).then(({ data }) => {
              if (data?.deleteSurveys > 0) {
                addSuccessSnackbar('Supprimé avec succès')
                setSelected([])
              }
            })
          }}
          handleTextFilterChange={(event) => setTextFilter(event.target.value)}
          textFilter={textFilter}
        />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              userRole={authState.role}
            />
            <TableBody>
              {stableSort(loading ? loadingUsers : rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .filter(filterRows)
                .length ?
                stableSort(loading ? loadingUsers : rows, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .filter(filterRows)
                  .map((row, index) => {
                    return (
                      <Row
                        key={row.id}
                        headers={headers}
                        isSelected={isSelected}
                        handleSelectClick={handleClick}
                        row={row}
                        index={index}
                        handleRowClick={handleRowClick}
                        userRole={authState.role}
                        downloadSurvey={downloadSurvey}
                      >
                      </Row>
                    )
                  }) : (
                  <TableRow>
                    <TableCell align="center" colSpan={10}>Aucune donnée</TableCell>
                  </TableRow>
                )}
              {emptyRows > 0 && (
                <TableRow style={{ height: (LINE_HEIGHT + 19 * 2) * emptyRows }}>
                  <TableCell colSpan={10} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 75]}
          component="div"
          count={rows.filter(filterRows).length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
          labelRowsPerPage={'Lignes par page :'}
        />
      </Paper>
    </div>
  )
}
